import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CreateSnapshotParams,
  CreateSnapshotResponse,
  CreateVolumeParams,
  CreateVolumeResponse,
  DeleteSnapshotParams,
  DeleteSnapshotResponse,
  DeleteVolumeParams,
  DeleteVolumeResponse,
  DeleteVolumesParams,
  DeleteVolumesResponse,
  ExtendVolumeParams,
  ExtendVolumeResponse,
  GetSnapshotParams,
  GetSnapshotResponse,
  GetSnapshotsParams,
  GetSnapshotsResponse,
  GetVolumeLimitsParams,
  GetVolumeLimitsResponse,
  GetVolumeParams,
  GetVolumeResponse,
  GetVolumesParams,
  GetVolumesResponse,
  GetVolumeTypeParams,
  GetVolumeTypeResponse,
  GetVolumeTypesParams,
  GetVolumeTypesResponse,
  Snapshot,
  UpdateSnapshotParams,
  UpdateSnapshotResponse,
  UpdateVolumeParams,
  UpdateVolumeResponse,
  Volume,
  VolumeLimits,
  VolumeType
} from "./types";

export type StorageState = {
  volumes: Volume[] | null;
  volume: Volume | null;
  isVolumeLoading: boolean;
  areVolumesLoading: boolean;
  isVolumeCreating: boolean;
  isVolumeUpdating: boolean;
  isVolumeExtending: boolean;
  isVolumeDeleting: boolean;
  areVolumesDeleting: boolean;
  snapshots: Snapshot[] | null;
  snapshot: Snapshot | null;
  isSnapshotLoading: boolean;
  areSnapshotsLoading: boolean;
  isSnapshotCreating: boolean;
  isSnapshotUpdating: boolean;
  isSnapshotDeleting: boolean;
  volumeTypes: VolumeType[] | null;
  volumeType: VolumeType | null;
  isVolumeTypeLoading: boolean;
  areVolumeTypesLoading: boolean;
  volumeLimits: VolumeLimits | null;
  areVolumeLimitsLoading: boolean;
};

const initialState: StorageState = {
  volumes: null,
  volume: null,
  isVolumeLoading: false,
  areVolumesLoading: false,
  isVolumeCreating: false,
  isVolumeUpdating: false,
  isVolumeExtending: false,
  isVolumeDeleting: false,
  areVolumesDeleting: false,
  snapshots: null,
  snapshot: null,
  isSnapshotLoading: false,
  areSnapshotsLoading: false,
  isSnapshotCreating: false,
  isSnapshotUpdating: false,
  isSnapshotDeleting: false,
  volumeTypes: null,
  volumeType: null,
  isVolumeTypeLoading: false,
  areVolumeTypesLoading: false,
  volumeLimits: null,
  areVolumeLimitsLoading: false
};

const storageSlice = createSlice({
  name: "storage",
  initialState,
  reducers: {
    // Get Volume
    getVolumeRequest: (state, action: PayloadAction<GetVolumeParams>) => {
      state.isVolumeLoading = true;
    },
    getVolumeSuccess: (state, action: PayloadAction<GetVolumeResponse>) => {
      state.volume = action.payload;
      state.isVolumeLoading = false;
    },
    getVolumeFailure: (
      state,
      action: PayloadAction<{ params: GetVolumeParams; error: string }>
    ) => {
      state.isVolumeLoading = false;
    },

    // Get Volumes
    getVolumesRequest: (state, action: PayloadAction<GetVolumesParams>) => {
      state.areVolumesLoading = true;
    },
    getVolumesSuccess: (state, action: PayloadAction<GetVolumesResponse>) => {
      state.volumes = action.payload;
      state.areVolumesLoading = false;
    },
    getVolumesFailure: (
      state,
      action: PayloadAction<{ params: GetVolumesParams; error: string }>
    ) => {
      state.areVolumesLoading = false;
    },

    // Create Volume
    createVolumeRequest: (state, action: PayloadAction<CreateVolumeParams>) => {
      state.isVolumeCreating = true;
    },
    createVolumeSuccess: (
      state,
      action: PayloadAction<CreateVolumeResponse>
    ) => {
      state.isVolumeCreating = false;
    },
    createVolumeFailure: (
      state,
      action: PayloadAction<{ params: CreateVolumeParams; error: string }>
    ) => {
      state.isVolumeCreating = false;
    },

    // Update Volume
    updateVolumeRequest: (state, action: PayloadAction<UpdateVolumeParams>) => {
      state.isVolumeUpdating = true;
    },
    updateVolumeSuccess: (
      state,
      action: PayloadAction<UpdateVolumeResponse>
    ) => {
      state.isVolumeUpdating = false;
    },
    updateVolumeFailure: (
      state,
      action: PayloadAction<{ params: UpdateVolumeParams; error: string }>
    ) => {
      state.isVolumeUpdating = false;
    },

    // Extend Volume
    extendVolumeRequest: (state, action: PayloadAction<ExtendVolumeParams>) => {
      state.isVolumeExtending = true;
    },
    extendVolumeSuccess: (
      state,
      action: PayloadAction<ExtendVolumeResponse>
    ) => {
      state.isVolumeExtending = false;
    },
    extendVolumeFailure: (
      state,
      action: PayloadAction<{ params: ExtendVolumeParams; error: string }>
    ) => {
      state.isVolumeExtending = false;
    },

    // Delete Volume
    deleteVolumeRequest: (state, action: PayloadAction<DeleteVolumeParams>) => {
      state.isVolumeDeleting = true;
    },
    deleteVolumeSuccess: (
      state,
      action: PayloadAction<DeleteVolumeResponse>
    ) => {
      state.isVolumeDeleting = false;
    },
    deleteVolumeFailure: (
      state,
      action: PayloadAction<{ params: DeleteVolumeParams; error: string }>
    ) => {
      state.isVolumeDeleting = false;
    },

    // Delete Volumes
    deleteVolumesRequest: (
      state,
      action: PayloadAction<DeleteVolumesParams>
    ) => {
      state.areVolumesDeleting = true;
    },
    deleteVolumesSuccess: (
      state,
      action: PayloadAction<DeleteVolumesResponse>
    ) => {
      state.areVolumesDeleting = false;
    },
    deleteVolumesFailure: (
      state,
      action: PayloadAction<{ params: DeleteVolumesParams; error: string }>
    ) => {
      state.areVolumesDeleting = false;
    },

    // Get Volume Limits
    getVolumeLimitsRequest: (
      state,
      action: PayloadAction<GetVolumeLimitsParams>
    ) => {
      state.areVolumeLimitsLoading = true;
    },
    getVolumeLimitsSuccess: (
      state,
      action: PayloadAction<GetVolumeLimitsResponse>
    ) => {
      state.volumeLimits = action.payload;
      state.areVolumeLimitsLoading = false;
    },
    getVolumeLimitsFailure: (
      state,
      action: PayloadAction<{ params: GetVolumeLimitsParams; error: string }>
    ) => {
      state.areVolumeLimitsLoading = false;
    },

    // Get Snapshot
    getSnapshotRequest: (state, action: PayloadAction<GetSnapshotParams>) => {
      state.isSnapshotLoading = true;
    },
    getSnapshotSuccess: (state, action: PayloadAction<GetSnapshotResponse>) => {
      state.snapshot = action.payload;
      state.isSnapshotLoading = false;
    },
    getSnapshotFailure: (
      state,
      action: PayloadAction<{ params: GetSnapshotParams; error: string }>
    ) => {
      state.isSnapshotLoading = false;
    },

    // Get Snapshots
    getSnapshotsRequest: (state, action: PayloadAction<GetSnapshotsParams>) => {
      state.areSnapshotsLoading = true;
    },
    getSnapshotsSuccess: (
      state,
      action: PayloadAction<GetSnapshotsResponse>
    ) => {
      state.snapshots = action.payload;
      state.areSnapshotsLoading = false;
    },
    getSnapshotsFailure: (
      state,
      action: PayloadAction<{ params: GetSnapshotsParams; error: string }>
    ) => {
      state.areSnapshotsLoading = false;
    },

    // Create Snapshot
    createSnapshotRequest: (
      state,
      action: PayloadAction<CreateSnapshotParams>
    ) => {
      state.isSnapshotCreating = true;
    },
    createSnapshotSuccess: (
      state,
      action: PayloadAction<CreateSnapshotResponse>
    ) => {
      state.isSnapshotCreating = false;
    },
    createSnapshotFailure: (
      state,
      action: PayloadAction<{ params: CreateSnapshotParams; error: string }>
    ) => {
      state.isSnapshotCreating = false;
    },

    // Update Snapshot
    updateSnapshotRequest: (
      state,
      action: PayloadAction<UpdateSnapshotParams>
    ) => {
      state.isSnapshotUpdating = true;
    },
    updateSnapshotSuccess: (
      state,
      action: PayloadAction<UpdateSnapshotResponse>
    ) => {
      state.isSnapshotUpdating = false;
    },
    updateSnapshotFailure: (
      state,
      action: PayloadAction<{ params: UpdateSnapshotParams; error: string }>
    ) => {
      state.isSnapshotUpdating = false;
    },

    // Delete Snapshot
    deleteSnapshotRequest: (
      state,
      action: PayloadAction<DeleteSnapshotParams>
    ) => {
      state.isSnapshotDeleting = true;
    },
    deleteSnapshotSuccess: (
      state,
      action: PayloadAction<DeleteSnapshotResponse>
    ) => {
      state.isSnapshotDeleting = false;
    },
    deleteSnapshotFailure: (
      state,
      action: PayloadAction<{ params: DeleteSnapshotParams; error: string }>
    ) => {
      state.isSnapshotDeleting = false;
    },

    // Get Volume Type
    getVolumeTypeRequest: (
      state,
      action: PayloadAction<GetVolumeTypeParams>
    ) => {
      state.isVolumeTypeLoading = true;
    },
    getVolumeTypeSuccess: (
      state,
      action: PayloadAction<GetVolumeTypeResponse>
    ) => {
      state.volumeType = action.payload;
      state.isVolumeTypeLoading = false;
    },
    getVolumeTypeFailure: (
      state,
      action: PayloadAction<{ params: GetVolumeTypeParams; error: string }>
    ) => {
      state.isVolumeTypeLoading = false;
    },

    // Get Volume Types
    getVolumeTypesRequest: (
      state,
      action: PayloadAction<GetVolumeTypesParams>
    ) => {
      state.areVolumeTypesLoading = true;
    },
    getVolumeTypesSuccess: (
      state,
      action: PayloadAction<GetVolumeTypesResponse>
    ) => {
      state.volumeTypes = action.payload;
      state.areVolumeTypesLoading = false;
    },
    getVolumeTypesFailure: (
      state,
      action: PayloadAction<{ params: GetVolumeTypesParams; error: string }>
    ) => {
      state.areVolumeTypesLoading = false;
    },

    // Clear helpers
    clearVolumes: (state) => {
      state.volumes = initialState.volumes;
    },
    clearSnapshots: (state) => {
      state.snapshots = initialState.snapshots;
    },
    clear: () => initialState
  }
});

export const {
  getVolumeRequest,
  getVolumeSuccess,
  getVolumeFailure,
  getVolumesRequest,
  getVolumesSuccess,
  getVolumesFailure,
  createVolumeRequest,
  createVolumeSuccess,
  createVolumeFailure,
  updateVolumeRequest,
  updateVolumeSuccess,
  updateVolumeFailure,
  extendVolumeRequest,
  extendVolumeSuccess,
  extendVolumeFailure,
  deleteVolumeRequest,
  deleteVolumeSuccess,
  deleteVolumeFailure,
  deleteVolumesRequest,
  deleteVolumesSuccess,
  deleteVolumesFailure,
  getVolumeLimitsRequest,
  getVolumeLimitsSuccess,
  getVolumeLimitsFailure,
  getSnapshotRequest,
  getSnapshotSuccess,
  getSnapshotFailure,
  getSnapshotsRequest,
  getSnapshotsSuccess,
  getSnapshotsFailure,
  createSnapshotRequest,
  createSnapshotSuccess,
  createSnapshotFailure,
  updateSnapshotRequest,
  updateSnapshotSuccess,
  updateSnapshotFailure,
  deleteSnapshotRequest,
  deleteSnapshotSuccess,
  deleteSnapshotFailure,
  getVolumeTypeRequest,
  getVolumeTypeSuccess,
  getVolumeTypeFailure,
  getVolumeTypesRequest,
  getVolumeTypesSuccess,
  getVolumeTypesFailure,
  clearVolumes,
  clearSnapshots,
  clear
} = storageSlice.actions;

export const { actions: storageActions } = storageSlice;

export const reducer = storageSlice.reducer;
